import { For } from "solid-js";

const APPLICATION_URL = "https://www.vgoautofinancing.com/application";

const vehicleCards = [
  {
    title: "Cars",
    description: "Reliable daily drivers and premium sedans for every stage of life.",
    image: "/vehicles/optimized/cars.webp",
  },
  {
    title: "Trucks",
    description: "Work-ready pickups with power, towing strength, and comfort.",
    image: "/vehicles/optimized/trucks.webp",
  },
  {
    title: "SUVs",
    description: "Room for family, cargo, and weekend adventures with confidence.",
    image: "/vehicles/optimized/suvs.webp",
  },
  {
    title: "Motorcycles",
    description: "Performance bikes and cruisers to match your riding style.",
    image: "/vehicles/optimized/motorcycles.webp",
  },
  {
    title: "Boats",
    description: "Weekend-ready watercraft for fishing, family, and fun.",
    image: "/vehicles/optimized/boats.webp",
  },
  {
    title: "Snowmobiles",
    description: "Winter machines built for thrill and control in Canadian weather.",
    image: "/vehicles/optimized/snowmobiles.webp",
  },
];

const trustStats = [
  "Fast pre-approvals in minutes",
  "All credit situations considered",
  "Canada-wide support",
  "No-pressure financing guidance",
];

const specialties = [
  "First-time buyers building credit",
  "Credit challenges and previous denials",
  "Newcomers with limited credit history",
  "Self-employed and non-traditional income",
  "Trade-ins with negative equity",
  "Seasonal workers and variable pay",
];

const steps = [
  { title: "Apply Online", detail: "Complete one quick form in under 3 minutes." },
  { title: "Get Matched", detail: "Our team compares lender options for your profile." },
  { title: "Pick Your Ride", detail: "Choose from cars, trucks, bikes, boats, and more." },
  { title: "Drive Away", detail: "Finalize terms and get the keys to your next vehicle." },
];

const testimonials = [
  {
    quote:
      "Two dealerships said no. Keys For All got me approved and in my truck the same week.",
    name: "D. Martin, Calgary",
  },
  {
    quote:
      "I had bruised credit after a rough year. The team treated me with respect and found a plan I could afford.",
    name: "J. Lee, Edmonton",
  },
  {
    quote:
      "I applied at lunch and had options by dinner. Smooth process and zero pressure.",
    name: "A. Patel, Winnipeg",
  },
];

const faqs = [
  {
    q: "Can I apply with bad credit?",
    a: "Yes. We work with partner lenders that consider many credit backgrounds, including recent setbacks.",
  },
  {
    q: "Is there a fee to apply?",
    a: "No. Your application is free and there is no obligation to accept an offer.",
  },
  {
    q: "How fast can I hear back?",
    a: "Many customers receive an update quickly, often the same day, depending on the application details.",
  },
  {
    q: "Do you only finance cars?",
    a: "No. We help with cars, trucks, SUVs, motorcycles, boats, and snowmobiles.",
  },
];

const CtaButton = (props) => (
  <a class={`cta-button ${props.secondary ? "secondary" : ""}`} href={APPLICATION_URL} target="_blank" rel="noreferrer">
    {props.text}
  </a>
);

const SectionHeader = (props) => (
  <div class="section-header">
    <p class="eyebrow">{props.eyebrow}</p>
    <h2>{props.title}</h2>
    <p>{props.subtitle}</p>
  </div>
);

export const App = () => (
  <main class="page">
    <section class="hero">
      <div class="hero-glow hero-glow-one" />
      <div class="hero-glow hero-glow-two" />
      <header class="site-header container">
        <div class="logo-wrap">
          <span class="logo-mark">KFA</span>
          <div>
            <p class="logo-name">Keys For All</p>
            <p class="logo-subtitle">Auto Finance Solutions</p>
          </div>
        </div>
        <CtaButton text="Get Approved Now" />
      </header>

      <div class="container hero-body">
        <div class="hero-copy">
          <p class="eyebrow">Fresh Start Auto Financing</p>
          <h1>
            Get The Keys To Your Next Vehicle, Even If Others Said No
          </h1>
          <p>
            Keys For All helps Canadians secure financing for cars, trucks,
            SUVs, motorcycles, boats, and snowmobiles. If your credit is bruised,
            new, or rebuilding, our lender network is designed to find a path
            forward.
          </p>
          <div class="hero-actions">
            <CtaButton text="Get Approved Now" />
            {/* <CtaButton text="See Your Options" secondary /> */}
          </div>
          <div class="trust-bar">
            <For each={trustStats}>{(item) => <span>{item}</span>}</For>
          </div>
        </div>
        <div class="hero-media">
          <img
            src="/optimized/hero.webp"
            alt="Premium vehicle lineup for auto financing"
            fetchpriority="high"
            decoding="async"
          />
        </div>
      </div>
    </section>

    <section class="section container">
      <SectionHeader
        eyebrow="Vehicle Selection"
        title="Financing For Every Type Of Ride"
        subtitle="From daily driving to weekend adventure, we connect you with lenders and inventory that match your goals."
      />
      <div class="vehicle-grid">
        <For each={vehicleCards}>
          {(vehicle) => (
            <article class="vehicle-card">
              <img
                src={vehicle.image}
                alt={`${vehicle.title} financing options`}
                loading="lazy"
                decoding="async"
              />
              <div class="vehicle-content">
                <h3>{vehicle.title}</h3>
                <p>{vehicle.description}</p>
              </div>
            </article>
          )}
        </For>
      </div>
    </section>

    <section class="section section-alt">
      <div class="container split">
        <div>
          <SectionHeader
            eyebrow="Why Choose Us"
            title="Built For Real People, Not Perfect Credit Reports"
            subtitle="Our process is clear, respectful, and focused on approvals that help you move forward."
          />
          <ul class="specialties">
            <For each={specialties}>{(item) => <li>{item}</li>}</For>
          </ul>
          <CtaButton text="Start My Application" />
        </div>
        <div class="steps-card">
          <h3>How It Works</h3>
          <For each={steps}>
            {(step, index) => (
              <div class="step-item">
                <span>{index() + 1}</span>
                <div>
                  <h4>{step.title}</h4>
                  <p>{step.detail}</p>
                </div>
              </div>
            )}
          </For>
        </div>
      </div>
    </section>

    <section class="section container">
      <SectionHeader
        eyebrow="Success Stories"
        title="Customers Who Got Their Fresh Start"
        subtitle="Proof that the right financing strategy can open the door when traditional routes fail."
      />
      <div class="testimonial-grid">
        <For each={testimonials}>
          {(entry) => (
            <article class="testimonial-card">
              <p>{entry.quote}</p>
              <h4>{entry.name}</h4>
            </article>
          )}
        </For>
      </div>
    </section>

    <section class="section section-alt">
      <div class="container faq-wrap">
        <SectionHeader
          eyebrow="FAQs"
          title="Questions About Approval?"
          subtitle="Here are the answers people ask before they apply."
        />
        <div class="faq-grid">
          <For each={faqs}>
            {(item) => (
              <article class="faq-item">
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </article>
            )}
          </For>
        </div>
      </div>
    </section>

    <section class="final-cta container">
      <div>
        <p class="eyebrow">Ready For Your Next Keys?</p>
        <h2>One Form Could Change Your Options Today</h2>
        <p>
          If banks or dealerships have turned you down, do not count yourself
          out. Submit your application and let Keys For All shop for solutions.
        </p>
      </div>
      <CtaButton text="Get Approved Now" />
    </section>

    <footer class="site-footer">
      <div class="container footer-content">
        <p>Keys For All • keysforall.ca</p>
        <a href={APPLICATION_URL} target="_blank" rel="noreferrer">
          Secure Application
        </a>
      </div>
    </footer>
  </main>
);
